import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { debounceTime, filter, finalize, switchMap, tap } from 'rxjs/operators';
import { Observable, of, Subject } from 'rxjs';
import { FormService } from '../../api/form.service';
import { SalesforceService } from '../../api/salesforce.service';
import { UserProfileService } from './../../api/user-profile.service';
import { TransactionService } from '../../api/transaction.service';
import { environment } from '../../../environments/environment';
import { TransactionTypes, RepresentativeTypes, TransactionType } from '../../models/transactions-enum';
import { OpportunityInstallmentObject } from '../../interfaces/data-objects';
import { Countries} from '../../utils/countries-enum';
import { DataObject } from '../../interfaces/data-objects';

@UntilDestroy()
@Component({
  selector: 'iv-create-invoice-modal-hkg',
  templateUrl: './create-invoice-modal-hkg.component.html',
  styleUrls: ['./create-invoice-modal-hkg.component.scss']
})
export class CreateInvoiceModalHkgComponent implements OnInit, OnDestroy {
  defaults = {
    rate_type: '',
  };
  opportunityTypes = TransactionTypes;
  resiFormModalData: DataObject[] = [];
  resiFormModalDataINDSGP: DataObject[] = [];
  selectedOpportunityType: TransactionType | null;
  representativeTypes = RepresentativeTypes;
  selectedRepresentativeType: string | null;

  opportunities: any = [];
  opportunityId: string | null = '';
  opportunitySpinner = false;
  submitSpinner = false;
  private opportunityIdSubject = new Subject<string>();

  createInvoiceModalForm: UntypedFormGroup;
  createInvoiceModalFormReset = false;

  teams: DataObject[] = [];
  teamId = '';
  private teamIdSubject = new Subject<string>();

  opportunityInstallmentId: string | null = '';
  private opportunityInstallmentsSubject = new Subject<OpportunityInstallmentObject>();
  opportunityInstallments$: Observable<any>;
  installmentsSpinner = false;
  showNa = true

  disableButton = true;
  searchOpportunityBy: 'name' | 'number' | 'na' = 'na';
  projectList = [];
  isSalesForceAPI = false;
  isCEPIntegration: boolean;
  hotelBusinessLine: boolean = false;
  defaultNaDisabled: boolean = true;
  residentialBusinessLine: boolean = false;
  ipMetroBusinessLine: boolean = false;
  
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialog: MatDialog,
    private router: Router,
    private formService: FormService,
    private fb: UntypedFormBuilder,
    private salesforceService: SalesforceService,
    private userProfileService: UserProfileService,
    private transactionService: TransactionService,
  ) {
    this.isCEPIntegration = this.userProfileService.getCEPIntegration();
    this.userProfileService.setInvoiceAddressFormUpdate('');
  }

  ngOnInit() {
    this.resiFormModalData = [
      {
        id: 'project', list: this.projectList, isSearch: false,
        isMultiSelect: false, placeHolderLabel: 'Search Project', selectedValues: '',
        label: 'Project', isRequired: true
      },
      {
        id: 'fee-type', list: this.projectList, isSearch: false, isMultiSelect: false,
        placeHolderLabel: 'Search', selectedValues: [], label: 'Fee Types',
        optionalLabel: 'Select all applicable', isRequired: true
      },
      {
        id: 'opportunities', list: this.projectList, isSearch: true, isMultiSelect: true,
        placeHolderLabel: 'Search', selectedValues: [], label: 'Opportunities',
        optionalLabel: 'Select all applicable opportunities related to this project', isRequired: true
      },
      {
        id: 'invoices', list: this.projectList, isSearch: true, isMultiSelect: true,
        placeHolderLabel: 'Search', selectedValues: [], label: 'Invoices',
        optionalLabel: 'Select all applicable', isRequired: true
      }
    ];
    this.resiFormModalDataINDSGP = [
      {
        id: 'opportunities', list: this.projectList, isSearch: true, isMultiSelect: true,
        placeHolderLabel: 'Search', selectedValues: [], label: 'Opportunities',
        optionalLabel: 'Select all applicable opportunities related to this project', isRequired: true
      }
    ]
    // If TRUE, Installments dropwdown needs to be displayed
    this.isSalesForceAPI = this.userProfileService.getSalesForceAPI();
    this.setupForm();
    this.getRateTypeOptions();
    this.setupTeamNamesSubscription();
  }

  setupForm() {
    const params = {
      opportunityId: [{
        value: this.opportunityId || null,
        disabled: true,
      }, Validators.required],
      teamId: [this.teamId || null, Validators.required],
      opportunityInstallmentId: [this.opportunityInstallmentId || '']
    };
    this.createInvoiceModalForm = this.fb.group(params);
    this.setupOpportunitiesSubscription();
    this.setupInstallmentsSubscription();
  }

  close(isSubmitted) {
    if (!isSubmitted) {
      this.data.onCloseTrigger = true;
    }
    this.dialog.getDialogById('invoicemodal')?.close(this.data);
  }

  selectOpportunityType(type) {
    // disabling only for prod
    if (['JPN', 'CHN', 'TWN'].includes(this.getUserCountry())) {
      if (type === 'dsf' && environment.disableDsfInProd) {
        this.selectedOpportunityType = null;
        return false;
      } else {
        this.selectedOpportunityType = type;
        this.disableCreateInvoice();
      }
    } else {
      this.selectedOpportunityType = type;
      this.disableCreateInvoice();
    }

    if (this.isClientIQIntegrationOn()) {
      this.searchOpportunityBy = 'name';
      this.createInvoiceModalForm.get('opportunityId')?.enable();
    }

    this.createInvoiceModalForm.get('opportunityId')?.markAsUntouched();

    if (this.data.opportunity.externalLink) {
      this.searchOpportunityBy = 'number';
      this.opportunityIdSubject.next(this.data.opportunity.number);
      this.createInvoiceModalForm.get('opportunityId')?.enable();
    }

    this.preFetchTeams();
  }

  selectRepresentativeType(type: string) {
    this.selectedRepresentativeType = type;
    this.disableCreateInvoice();
  }

  back() {
    this.hotelBusinessLine = false;
    this.selectedOpportunityType = null;
    this.selectedRepresentativeType = null;
    this.createInvoiceModalForm.patchValue({
      opportunityId: null,
      teamId: null
    }, { emitEvent: false });
    this.opportunities = [];
    this.teamId = '';
    this.disableButton = true;
    this.searchOpportunityBy = 'na';
    this.createInvoiceModalForm.get('opportunityId')?.disable();
    this.createInvoiceModalForm.get('teamId')?.markAsUntouched();
  }

  disableCreateInvoice() {
    switch (this.selectedOpportunityType) {
      case TransactionTypes.DSF:
      case TransactionTypes.SALE:
      case TransactionTypes.LEASE:
      case TransactionTypes.VALUATION:
        if (!this.selectedRepresentativeType ||
          (this.teamId === '' || this.teamId === undefined || this.teamId === null)) {
          this.disableButton = true;
        } else if (this.selectedRepresentativeType && this.teamId) {
          this.disableButton = false;
        }
        break;
      case TransactionTypes.CONSULTING:
      case TransactionTypes.EXPENSE:
        if (this.teamId === '' || this.teamId === undefined || this.teamId === null) {
          this.disableButton = true;
        } else {
          this.disableButton = false;
        }
        break;
      case TransactionTypes.RESI:
        if (this.teamId === '' || this.teamId === undefined || this.teamId === null) {
          this.disableButton = true;
        } else {
          this.disableButton = false;
        }
        break;
      default:
        this.disableButton = true;
        break;
    }
  }

  createInvoice() {
    this.submitSpinner = true;
    this.disableButton = true;
    switch (this.selectedOpportunityType) {
      case this.opportunityTypes.RESI:
        this.transactionService.createTransaction({
          opportunity_id: this.opportunityId,
          rep_role: this.selectedRepresentativeType,
          rate_type: this.defaults.rate_type,
          team: this.teamId,
          installment_id: this.opportunityInstallmentId,
          transaction_type: TransactionTypes.RESI,
        }).subscribe((result) => {
          this.handleResult(result, `/${TransactionTypes.RESI}`);
        });
        break;
      case this.opportunityTypes.LEASE:
        this.transactionService.createTransaction({
          opportunity_id: this.opportunityId,
          opportunity_number: this.data?.opportunity?.number || '',
          rep_role: this.selectedRepresentativeType,
          rate_type: this.defaults.rate_type,
          team: this.teamId,
          installment_id: this.opportunityInstallmentId,
          is_deeplinking_transaction: this.isDeepLinkingTransaction(),
          source_system: this.data.opportunity.sourceSystem,
          transaction_type: TransactionTypes.LEASE,
          is_residential_business_line: this.residentialBusinessLine
        }).subscribe((result) => {
          this.handleResult(result, `/${TransactionTypes.LEASE}`);
        });
        break;
      case this.opportunityTypes.SALE:
        this.transactionService.createTransaction({
          opportunity_id: this.opportunityId,
          opportunity_number: this.data?.opportunity?.number || '',
          rep_role: this.selectedRepresentativeType,
          rate_type: this.defaults.rate_type,
          team: this.teamId,
          installment_id: this.opportunityInstallmentId,
          is_deeplinking_transaction: this.isDeepLinkingTransaction(),
          source_system: this.data.opportunity.sourceSystem,
          is_hotels_business_line: this.isCEPIntegration && ['AUS', 'NZL', 'IND'].includes(this.getUserCountry())
            ? this.hotelBusinessLine : false,
          transaction_type: TransactionTypes.SALE,
          is_residential_business_line: this.residentialBusinessLine,
          is_ip_metro_business_line: this.ipMetroBusinessLine
        }).subscribe((result) => {
          this.handleResult(result, `/${TransactionTypes.SALE}`);
        });
        break;
      case this.opportunityTypes.VALUATION:
        this.transactionService.createTransaction({
          opportunity_id: this.opportunityId,
          rep_role: this.selectedRepresentativeType,
          rate_type: this.defaults.rate_type,
          team: this.teamId,
          installment_id: this.opportunityInstallmentId,
          is_deeplinking_transaction: this.isDeepLinkingTransaction(),
          source_system: this.data.opportunity.sourceSystem,
          transaction_type: TransactionTypes.VALUATION,
        }).subscribe((result) => {
          this.handleResult(result, `/${TransactionTypes.VALUATION}`);
        });
        break;
      case this.opportunityTypes.EXPENSE:
        this.transactionService.createTransaction({
          rate_type: this.defaults.rate_type,
          team: this.teamId,
          installment_id: this.opportunityInstallmentId,
          transaction_type: TransactionTypes.EXPENSE,
        }).subscribe((result) => {
          this.handleResult(result, `/${TransactionTypes.DISBURSEMENT}`);
        });
        break;
      case this.opportunityTypes.CONSULTING:
        this.transactionService.createTransaction({
          opportunity_id: this.opportunityId,
          opportunity_number: this.data?.opportunity?.number || '',
          team: this.teamId,
          installment_id: this.opportunityInstallmentId,
          is_deeplinking_transaction: this.isDeepLinkingTransaction(),
          source_system: this.data.opportunity.sourceSystem,
          transaction_type: TransactionTypes.CONSULTING,
        }).subscribe((result) => {
          this.handleResult(result, `/${TransactionTypes.CONSULTING}`);
        });
        break;
      case this.opportunityTypes.DSF:
        this.transactionService.createTransaction({
          opportunity_id: this.opportunityId,
          opportunity_number: this.data?.opportunity?.number || '',
          rep_role: this.selectedRepresentativeType,
          rate_type: this.defaults.rate_type,
          team: this.teamId,
          installment_id: this.opportunityInstallmentId,
          is_deeplinking_transaction: this.isDeepLinkingTransaction(),
          source_system: this.data.opportunity.sourceSystem,
          transaction_type: TransactionTypes.DSF,
        }).subscribe((result) => {
          this.handleResult(result, `/${TransactionTypes.DSF}`);
        });
        break;
      default:
        console.log('Submitting unsupported opportunityType: ', this.selectOpportunityType);
        break;
    }
  }

  createExpense() {
    this.selectOpportunityType(this.opportunityTypes.EXPENSE);
  }

  handleResult(result, redirectPath) {
    const id = result.transactionId || result.id;
    if (id) {
      this.router.navigate([redirectPath, id]);
      this.close(true);
    } else {
      this.submitSpinner = false;
      this.disableButton = false;
      console.log('Create Invoice Result had no ID.', result);
    }
  }

  getRateTypeOptions() {
    this.formService.getFieldOptions('transaction', 'rate_type')
      .pipe(untilDestroyed(this))
      .subscribe(options => {
        this.defaults.rate_type = options[0].value;
      });
  }

  representDeal() {
    let showDealFlag = false;
    switch (this.selectedOpportunityType) {
      case this.opportunityTypes.CONSULTING:
      case this.opportunityTypes.RESI:
        showDealFlag = false; break;
      default:
        showDealFlag = true; break;
    }
    return showDealFlag;
  }

  setupOpportunitiesSubscription() {
    this.opportunityIdSubject
      .pipe(untilDestroyed(this),
        filter((change: any) => change && change.length >= 3),
        debounceTime(500),
        switchMap((change) => {
          if (!this.opportunityId) {
            this.opportunitySpinner = true;
          }
          return this.salesforceService.getOpportunities(change, this.searchOpportunityBy, this.data.opportunity.externalLink, this.data.opportunity.sourceSystem)
            .pipe(
              tap({
                next: (_oppurtunities) => {
                  if (_oppurtunities) {
                    this.opportunities = _oppurtunities;
                    if (_oppurtunities && _oppurtunities.length === 1 && this.data.opportunity.externalLink) {
                      this.opportunityId = _oppurtunities[0].opportunity_id;
                      this.data.opportunity.name = _oppurtunities[0]['opp_name'];
                      this.data.opportunity.number = _oppurtunities[0]['opportunity_number'];
                      this.opportunityInstallmentsSubject.next({'opportunityId': this.opportunityId, 'opportunityNumber': this.data.opportunity.number, 'sourceSystem': this.data.opportunity.sourceSystem});
                      this.createInvoiceModalForm.patchValue({
                        opportunityId: this.currentOppVal(_oppurtunities[0])
                      }, { emitEvent: false });
                      this.createInvoiceModalForm.get('opportunityId')?.disable();
                    }
                  } else {
                    this.opportunityId = '';
                  }
                  this.opportunitySpinner = false;
                }
              })
            )
        })
      ).subscribe();
  }

  setupInstallmentsSubscription() {
    this.opportunityInstallments$ = this.opportunityInstallmentsSubject
      .pipe(
        tap(() => {
          this.installmentsSpinner = true;
        }),
        switchMap((params: OpportunityInstallmentObject) => {
          if (params.opportunityId) {
            return this.salesforceService.getInstallments(params)
              .pipe(
                tap((installments) => {
                  if (installments.length > 0) {
                    this.opportunityInstallmentId = installments[0].value;
                    this.createInvoiceModalForm.controls.opportunityInstallmentId.setValue(this.opportunityInstallmentId, {
                      emitEvent: false
                    });
                  }
                }),
                finalize(() => { this.installmentsSpinner = false; })
              );
          }
          this.installmentsSpinner = false;
          return of([]);
        })
      );
  }

  filterOpportunities(opportunityId: string | null) {
    return this.opportunities.filter((opportunity: any) => opportunity.opportunity_id === opportunityId);
  }

  currentOppVal(opportunity: any) {
    if (opportunity) {
      let optionValue = opportunity.opportunity_number + ` (${opportunity.opp_name})`;
      return this.searchOpportunityBy === 'number' ? optionValue : opportunity.opp_name;
    }
  }

  opportunitySelectionChange(opportunity: any, event: any) {
    this.data.opportunity.name = opportunity['opp_name']
    this.data.opportunity.number = opportunity['opportunity_number'];
    // Ignore the deselection event
    if (!event.isUserInput) {
      return;
    }
    if (this.opportunityId !== opportunity.opportunity_id) {
      this.opportunityId = opportunity.opportunity_id;
      this.opportunities = this.filterOpportunities(this.opportunityId);
      this.opportunityInstallmentsSubject.next({
        'opportunityId': this.opportunityId,
        'opportunityNumber': this.data.opportunity.number,
        'sourceSystem': this.data.opportunity.sourceSystem
      });
    }
  }

  installmentsSelectionChange(installmentId: string, event: any) {
    // Ignore the deselection event
    if (!event.isUserInput) {
      return;
    }
    this.opportunityInstallmentId = installmentId;
  }

  opportunityIdInput(data: string) {
    this.opportunityIdSubject.next(data);
    if (!this.opportunityId || this.opportunityId !== this.createInvoiceModalForm.get('opportunityId')?.value) {
      this.createInvoiceModalForm.controls.opportunityId.setErrors({ 'opportunityIdEmpty': true });
      this.opportunityId = null;
      this.opportunityInstallmentId = null;
      this.opportunityInstallmentsSubject.next({
        'opportunityId': null,
        'opportunityNumber': null,
        'sourceSystem': null,
      });
    }
  }

  preFetchTeams() {
    this.userProfileService.getTeamNames()
      .pipe(untilDestroyed(this))
      .subscribe((response: any[]) => this.teams = response);
  }

  setupTeamNamesSubscription() {
    this.teamIdSubject
      .pipe(
        untilDestroyed(this),
        tap(() => {
          this.teamId = '';
          this.disableCreateInvoice();
        }),
        debounceTime(500),
        switchMap((userInput: string) => this.userProfileService.getTeamNames(null, userInput))
      )
      .subscribe((response: any) => this.teams = response);
  }

  teamIdInput(userInput: string) {
    this.teamIdSubject.next(userInput);
  }

  teamSelectionChange(team: any, event: any) {
    // Ignore the deselection event
    if (!event.isUserInput) {
      return;
    }
    this.teamId = team?.id;
    this.selectedOpportunityType === TransactionTypes.VALUATION ?  this.selectedRepresentativeType = 'client' : undefined;
    this.defaultNaDisabled = false;
    if(team){
      this.showOrHideNaOption(team)
    }

    this.disableCreateInvoice();
  }

  getUserCountry() {
    return this.userProfileService.getCountryLocalStorage();
  }

  onSearchCriteriaChange() {
    this.data.opportunity.externalLink ? this.fromSalesforce() : this.fromFinishline();
  }

  fromSalesforce() {
    const key = this.searchOpportunityBy;
    const oppname = this.data.opportunity.name;
    const oppnumber = this.data.opportunity.number;
    const oppnumberoption = `${oppnumber}(${oppname})`;
    const opportunityIdControl = this.createInvoiceModalForm.get('opportunityId');

    switch (key) {
      case 'name':
        opportunityIdControl?.disable();
        if (!this.createInvoiceModalFormReset) {
          opportunityIdControl?.setValue(oppname, { emitEvent: false });
        } else {
          this.opportunityIdSubject.next(oppname);
          this.createInvoiceModalFormReset = false;
        }
        break;
      case 'number':
        opportunityIdControl?.disable();
        if (!this.createInvoiceModalFormReset) {
          opportunityIdControl?.setValue(oppnumberoption, { emitEvent: false })
        } else {
          this.opportunityIdSubject.next(oppnumber);
          this.createInvoiceModalFormReset = false;
        }
        break;
      case 'na':
        this.createInvoiceModalForm.patchValue({
          opportunityId: '',
          opportunityInstallmentId: ''
        }, { emitEvent: false });

        this.opportunityId = '';
        this.opportunities = [];
        this.opportunityInstallmentId = '';
        this.data.opportunity.number = '';
        this.data.opportunity.name = null;
        this.data.opportunity.sourceSystem = null;
        opportunityIdControl?.disable();
        this.opportunityInstallmentsSubject.next({
          'opportunityId': null,
          'opportunityNumber': null,
          'sourceSystem': null,
        });
        this.createInvoiceModalFormReset = true;
        break;
      default:
        break;
    }
  }

  fromFinishline() {
    this.createInvoiceModalForm.patchValue({
      opportunityId: '',
      opportunityInstallmentId: ''
    }, { emitEvent: false });

    this.opportunityId = '';
    this.opportunities = [];
    this.opportunityInstallmentId = null;
    this.data.opportunity.number = '';
    this.data.opportunity.name = null;
    this.data.opportunity.sourceSystem = null;

    if (this.searchOpportunityBy === 'na') {
      this.createInvoiceModalForm.get('opportunityId')?.disable();
    } else {
      this.createInvoiceModalForm.get('opportunityId')?.enable();
      this.opportunityInstallmentsSubject.next({
        'opportunityId': null,
        'opportunityNumber': null,
        'sourceSystem': null,
      });
    }
  }

  hideDisbursement(): boolean {
    return (this.getUserCountry() === 'IND' ? false : true);
  }

  isClientIQIntegrationOn(): boolean {
    return this.userProfileService.getClientIQIntegration(this.selectedOpportunityType || '');
  }

  isDeepLinkingTransaction(): boolean {
    return this.data.opportunity.externalLink && this.searchOpportunityBy !== 'na';
  }

  saveHotelBusinessLine(): void {
    this.hotelBusinessLine = !this.hotelBusinessLine;
    // Disable residential business line when hotel business line is checked
    if (this.hotelBusinessLine) {
      this.residentialBusinessLine = false;
    }
  }

  saveResidentialBusinessLine(): void {
    this.residentialBusinessLine = !this.residentialBusinessLine;
    // Disable hotel business line when residential business line is checked
    if (this.residentialBusinessLine) {
      this.hotelBusinessLine = false;
    }
  }

  saveIpMetroBusinessLine(): void {
    this.ipMetroBusinessLine = !this.ipMetroBusinessLine;
    // Disable hotel business line when IP Metro business line is checked
    if (this.ipMetroBusinessLine) {
      this.hotelBusinessLine = false;
    }
  }

  isPacificIsolationEnable(): boolean {
    return this.userProfileService.isPacificIsolationEnable();
  }

  isResidentialBusinessLineEnabled(): boolean {
    return (
      [Countries.India, Countries.Singapore].includes(this.getUserCountry()) && 
      (this.selectedOpportunityType === TransactionTypes.LEASE || this.selectedOpportunityType === TransactionTypes.SALE)
    );
  }

  isValuationEnabled(): boolean {
    return this.userProfileService.getIsValuationEnabled();
  }

  isIpMetroBusinessLineEnabled(): boolean {
    return this.selectedOpportunityType === TransactionTypes.SALE && this.getUserCountry() === Countries.Australia;
  }

  showOrHideNaOption(team): boolean {
    if (
      (this.getUserCountry() === Countries.Singapore || (this.getUserCountry() === Countries.India)) &&
      this.isClientIQIntegrationOn()
    ) {
      this.showNa = !this.userProfileService.getTeamsClientIQIntegration(this.selectedOpportunityType || '', team);
      if(!this.showNa){
        this.searchOpportunityBy = 'name';
      }
      return this.showNa
    } else {
      this.showNa =  !this.isClientIQIntegrationOn();
      return  this.showNa
    }

  }

  isHotelBusinessLineDisabled(): boolean {
    return this.residentialBusinessLine || this.ipMetroBusinessLine;
  }

  isResidentialBusinessLineDisabled(): boolean {
    return this.hotelBusinessLine;
  }

  isIpMetroBusinessLineDisabled(): boolean {
    return this.hotelBusinessLine;
  }

  ngOnDestroy() {
  }

}
